(() => {
  'use strict';
  const location = window.DOIEmbeddedContext?.location || window.location;
  const origin = location.origin;
  const cache = new Map();
  const requests = new Set();
  const privatePages = new Set(['my-doi','my-home-search','my-desert-home','nature-journal','my-trips','coachella-valley-interactive-map','coachella-valley-atlas']);
  function resource(value) {
    try {
      const u = new URL(value, origin);
      if (u.origin !== origin || u.username || u.password || !/^\/(?:products\/[^/]+|collections\/[^/]+|pages\/[^/]+|blogs\/[^/]+\/[^/]+)\/?$/.test(u.pathname)) return null;
      if (u.pathname.startsWith('/pages/') && privatePages.has(u.pathname.split('/')[2])) return null;
      return u.pathname.replace(/\/$/, '') + u.hash;
    } catch { return null; }
  }
  function normalize(value, alt = '', source = '') {
    const raw = typeof value === 'string' ? value : value?.url || value?.src;
    try {
      const u = new URL(raw, origin);
      if (u.protocol !== 'https:' || u.username || u.password || !(u.origin === origin || u.hostname === 'cdn.shopify.com')) return null;
      if (!/\.(?:jpe?g|png|webp|avif|gif)(?:$)/i.test(u.pathname)) return null;
      for (const name of ['width','height','crop','pad_color']) u.searchParams.delete(name);
      const parts = u.pathname.split('/');
      const file = decodeURIComponent(parts.pop()).replace(/_(?:\d+x\d*|\d*x\d+|small|medium|large|grande|master|compact|thumb)(?=\.[^.]+$)/i, '');
      const bucket = parts.includes('articles') ? 'articles' : parts.includes('products') ? 'products' : 'files';
      return {url:u.href, key:bucket+'/'+file.toLowerCase(), alt:String(value?.alt || alt || '').slice(0,400), source};
    } catch { return null; }
  }
  function merge(...lists) {
    const seen = new Set(), photos = [];
    for (const item of lists.flat()) {
      const photo = normalize(item, item?.alt, item?.source);
      if (photo && !seen.has(photo.key)) { seen.add(photo.key); photos.push(photo); }
    }
    return photos;
  }
  function fromDocument(doc, link) {
    const path = resource(link); if (!path) return [];
    const u = new URL(path, origin), photos = [];
    const add = (value, alt) => { const p=normalize(value, alt, path); if(p)photos.push(p); };
    if (!u.hash) add(doc.querySelector('meta[property="og:image"]')?.content, doc.querySelector('meta[property="og:title"]')?.content);
    if (u.pathname.startsWith('/collections/')) return merge(photos);
    let scope;
    if (u.hash) {
      let anchor; try { anchor=doc.getElementById(decodeURIComponent(u.hash.slice(1))); } catch {}
      scope=anchor?.closest('[data-restaurant-id],.restaurant-card,article') || anchor;
    } else if (u.pathname.startsWith('/blogs/')) {
      scope=doc.querySelector('article.doi-native,article[data-doi-structure],.article-template,main article');
    } else {
      scope=doc.querySelector('main');
    }
    if (!scope) return merge(photos);
    const selectors = u.pathname.startsWith('/blogs/') || u.hash ? 'img' : '.rte img,[data-doi-main-content] img,[data-gallery] img,[data-photo-gallery] img,.doi-gallery img,.doi-place-gallery img,.doi-entry__gallery img,.product__media img';
    for (const img of scope.querySelectorAll(selectors)) {
      if (img.closest('nav,footer,aside,form,.doi-author,.doi-related,.doi-keep-exploring,.doi-explore-around,[data-doi-curated-related],[data-doi-reviews],.md-root')) continue;
      const enclosing = img.closest('a[href]')?.getAttribute('href');
      const original = img.getAttribute('data-doi-original-src') || img.getAttribute('data-full-image') || img.getAttribute('data-original');
      const candidates = (img.getAttribute('data-srcset') || img.getAttribute('srcset') || '').split(',').map(s=>s.trim().split(/\s+/)).filter(s=>s[0]).sort((a,b)=>(parseFloat(a[1])||0)-(parseFloat(b[1])||0));
      const url = original || (enclosing && normalize(enclosing) ? enclosing : null) || candidates.at(-1)?.[0] || img.getAttribute('data-src') || img.getAttribute('src');
      add(url, img.getAttribute('alt') || img.closest('figure')?.querySelector('figcaption')?.textContent?.trim());
    }
    return merge(photos);
  }
  function remember(link, doc) {
    const key = resource(link);
    if (key && !key.startsWith('/products/') && !cache.has(key)) cache.set(key, Promise.resolve(fromDocument(doc, key)));
  }
  async function fetchPayload(url, json = false) {
    const controller = new AbortController(), timeout = setTimeout(()=>controller.abort(),12000);
    requests.add(controller);
    try {
      const response = await fetch(url, {credentials:'omit',signal:controller.signal});
      if (!response.ok) throw Error('Gallery source unavailable');
      return await (json ? response.json() : response.text());
    } finally { clearTimeout(timeout); requests.delete(controller); }
  }
  function read(link) {
    const key = resource(link); if (!key) return Promise.resolve([]);
    if (cache.has(key)) return cache.get(key);
    const pending = (async()=>{
      const u = new URL(key, origin);
      if (u.pathname.startsWith('/products/')) {
        const product = await fetchPayload(u.pathname+'.js',true);
        const media = (product.media || []).filter(m=>m.media_type==='image');
        const images = media.length ? media.map(m=>({url:m.src||m.preview_image?.src,alt:m.alt||product.title})) : (product.images||[]).map(url=>({url,alt:product.title}));
        return merge(images.map(image=>({...image,source:key})));
      }
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) u.searchParams.set('preview_theme_id',preview);
      const markup = await fetchPayload(u.pathname+u.search);
      const doc = new DOMParser().parseFromString(markup,'text/html');
      return fromDocument(doc,key);
    })().catch(error=>{cache.delete(key);throw error;});
    cache.set(key,pending);
    if(cache.size>64)cache.delete(cache.keys().next().value);
    return pending;
  }
  async function load(resources) {
    const links=[...new Set(resources.filter(r=>r.group!=='floor_plans').map(r=>resource(r.url)).filter(Boolean))];
    const results=new Array(links.length);let next=0,failed=0;
    async function worker(){while(next<links.length){const i=next++;try{results[i]=await read(links[i]);}catch{failed++;results[i]=[];}}}
    await Promise.all(Array.from({length:Math.min(3,links.length)},worker));
    return {photos:merge(...results),partial:failed>0};
  }
  window.DOIMapGallery={normalize,merge,remember,load,abort:()=>{for(const request of requests)request.abort();requests.clear();}};
})();
